import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { downloadCsv } from "../lib/download";
import { Flame, RefreshCw, Loader2, Download } from "lucide-react";
import logger from "../lib/logger";

const WEEK_OPTIONS = [4, 8, 12];

// Cell colour helper: green = met, red = missed.
// Ratio shades intensity so partial weeks read as "warning" rather than total miss.
function cellStyle(weekCell) {
  if (weekCell.met) {
    return { bg: "#16a34a", fg: "#FFFFFF", border: "#15803d" }; // green-600
  }
  const ratio = weekCell.ratio || 0;
  if (ratio >= 0.66) {
    return { bg: "#f59e0b", fg: "#1f2937", border: "#d97706" }; // amber
  }
  if (ratio >= 0.33) {
    return { bg: "#ef4444", fg: "#FFFFFF", border: "#b91c1c" }; // red-500
  }
  return { bg: "#7f1d1d", fg: "#FFFFFF", border: "#450a0a" }; // red-900 (chronic dark)
}

function formatRange(week) {
  const fmt = (d) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
    });
  return `${fmt(week.week_start)} – ${fmt(week.week_end)}`;
}

export default function SLAHeatmap() {
  const [weeksWanted, setWeeksWanted] = useState(4);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/sla-heatmap", {
        params: { weeks: weeksWanted },
      });
      setData(data);
    } catch (err) {
      logger.error("Heatmap fetch failed:", err);
      toast.error("Could not load SLA heatmap");
    } finally {
      setLoading(false);
    }
  }, [weeksWanted]);

  useEffect(() => {
    load();
  }, [load]);

  const onExportCsv = useCallback(async () => {
    try {
      await downloadCsv(
        "/reports/sla-heatmap.csv",
        { weeks: weeksWanted },
        `sla_heatmap_${weeksWanted}w.csv`,
      );
      toast.success("SLA heatmap CSV downloaded");
    } catch (err) {
      logger.error("CSV download failed", err);
      toast.error("Could not download CSV");
    }
  }, [weeksWanted]);

  const totals = useMemo(() => {
    if (!data) return { sites: 0, chronicSites: 0, taskWeeks: 0, missedTaskWeeks: 0 };
    let taskWeeks = 0;
    let missedTaskWeeks = 0;
    let chronicSites = 0;
    data.sites.forEach((s) => {
      if (s.chronic_task_count > 0) chronicSites += 1;
      s.tasks.forEach((t) => {
        t.weekly.forEach((w) => {
          taskWeeks += 1;
          if (!w.met) missedTaskWeeks += 1;
        });
      });
    });
    return { sites: data.sites.length, chronicSites, taskWeeks, missedTaskWeeks };
  }, [data]);

  return (
    <div className="space-y-5" data-testid="sla-heatmap">
      <div className="flex flex-wrap items-end justify-between gap-3 border border-gray-300 bg-white p-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500">
            Compliance · SLA Heatmap
          </div>
          <h2 className="text-2xl font-black tracking-tighter">
            Task completion across the last {weeksWanted} weeks
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Green = weekly target met. Amber/red = short of target. Dark red = far below or chronic.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-gray-300" role="group">
            {WEEK_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setWeeksWanted(n)}
                data-testid={`heatmap-weeks-${n}`}
                className={`px-3 py-1.5 text-xs font-semibold ${
                  weeksWanted === n
                    ? "bg-[#002FA7] text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {n}w
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="heatmap-refresh"
            className="px-3 py-1.5 border border-gray-300 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-100 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Refresh
          </button>
          <button
            onClick={onExportCsv}
            data-testid="heatmap-export-csv"
            className="px-3 py-1.5 border border-gray-800 bg-gray-900 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-black"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-300">
        <SummaryTile label="Sites tracked" value={totals.sites} testid="heatmap-summary-sites" />
        <SummaryTile
          label="Chronic sites"
          value={totals.chronicSites}
          accent={totals.chronicSites > 0 ? "#b91c1c" : undefined}
          testid="heatmap-summary-chronic"
        />
        <SummaryTile
          label="Task-weeks tracked"
          value={totals.taskWeeks}
          testid="heatmap-summary-taskweeks"
        />
        <SummaryTile
          label="Missed task-weeks"
          value={totals.missedTaskWeeks}
          accent={totals.missedTaskWeeks > 0 ? "#b91c1c" : undefined}
          testid="heatmap-summary-missed"
        />
      </div>

      {/* Grid */}
      <div className="border border-gray-300 bg-white overflow-x-auto">
        {loading && !data && (
          <div className="p-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading heatmap…
          </div>
        )}

        {data && data.sites.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="heatmap-empty">
            No sites with SLA-targeted cleaning tasks yet. Add a workplace type
            (Office, Gym, Medical Centre, etc.) to a site to start tracking SLAs.
          </div>
        )}

        {data && data.sites.length > 0 && (
          <table className="min-w-full text-xs" data-testid="heatmap-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-300">
                <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-gray-50 z-10">
                  Site / Task
                </th>
                <th className="text-center px-2 py-2 font-semibold w-16">Target</th>
                {data.weeks.map((w) => (
                  <th
                    key={w.week_start}
                    className="text-center px-2 py-2 font-semibold whitespace-nowrap"
                  >
                    {formatRange(w)}
                  </th>
                ))}
                <th className="text-center px-2 py-2 font-semibold w-16">Miss</th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((site) => (
                <SiteRows key={site.site_id} site={site} weeks={data.weeks} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
        <LegendSwatch color="#16a34a" label="Met (≥ target)" />
        <LegendSwatch color="#f59e0b" label="Close (≥ 66%)" />
        <LegendSwatch color="#ef4444" label="Short (≥ 33%)" />
        <LegendSwatch color="#7f1d1d" label="Severe (< 33%)" />
        <span className="inline-flex items-center gap-1 ml-2">
          <Flame className="w-3.5 h-3.5 text-red-700" /> Chronic = missed every week in range
        </span>
      </div>
    </div>
  );
}

function SiteRows({ site, weeks }) {
  return (
    <>
      <tr className="bg-gray-100 border-b border-gray-200">
        <td
          colSpan={3 + weeks.length}
          className="px-3 py-1.5 text-xs sticky left-0 bg-gray-100"
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 border border-gray-400"
              style={{ backgroundColor: site.site_color?.bg || "#E5E7EB" }}
            />
            <span className="font-semibold">{site.site_name}</span>
            {site.site_type && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500">
                · {site.site_type}
              </span>
            )}
            {site.chronic_task_count > 0 && (
              <span
                className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300 text-[10px] font-semibold"
                data-testid={`heatmap-site-${site.site_id}-chronic`}
              >
                <Flame className="w-3 h-3" /> {site.chronic_task_count} chronic task
                {site.chronic_task_count > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </td>
      </tr>
      {site.tasks.map((task) => (
        <tr key={task.task_id} className="border-b border-gray-100">
          <td className="px-3 py-1.5 pl-8 text-gray-800 sticky left-0 bg-white">
            {task.task_label}
            {task.chronic && (
              <Flame
                className="inline-block w-3 h-3 ml-1.5 text-red-700"
                data-testid={`heatmap-task-${site.site_id}-${task.task_id}-chronic`}
              />
            )}
          </td>
          <td className="text-center px-2 py-1.5 text-gray-500 font-mono">
            {task.target}/wk
          </td>
          {task.weekly.map((w) => {
            const style = cellStyle(w);
            return (
              <td
                key={w.week_index}
                className="text-center px-1 py-1"
                data-testid={`heatmap-cell-${site.site_id}-${task.task_id}-${w.week_index}`}
              >
                <div
                  title={`${w.actual} of ${w.target} (${formatRange({
                    week_start: w.week_start,
                    week_end: w.week_start,
                  })})`}
                  className="mx-auto w-full max-w-[88px] py-1.5 text-[11px] font-bold border"
                  style={{
                    backgroundColor: style.bg,
                    color: style.fg,
                    borderColor: style.border,
                  }}
                >
                  {w.actual}/{w.target}
                </div>
              </td>
            );
          })}
          <td className="text-center px-2 py-1.5 font-mono text-gray-700">
            {task.missed_count}
          </td>
        </tr>
      ))}
    </>
  );
}

function SummaryTile({ label, value, accent, testid }) {
  return (
    <div className="bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div
        className="text-2xl font-black tracking-tighter mt-1"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-3 border border-gray-400"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}
